import { useState, useCallback, useRef } from 'react';
import { ChatMessage, ChatSuggestion } from './types';

const MOCK_RESPONSES: Record<string, string> = {
    default: `I'm the OrcaWeb AI assistant. I can help you with:

- **Navigating OrcaWeb** — finding pages, understanding features
- **Vessel operations** — report events, cargo temperature, stowage
- **Data questions** — understanding your fleet data

*This is a preview with mock responses. Real AI integration is coming soon.*`,

    help: `Here are some things I can help with:

1. **Report Events** — How to create, edit, and submit voyage events
2. **Stowage** — Tank plan management and cargo operations
3. **Cargo Temperature** — Temperature monitoring and alerts
4. **AOL (Arrival & Operations Log)** — Daily reporting workflow
5. **Settings** — User preferences and vessel configuration

Just ask me a question about any of these topics!`,

    stowage: `The **Stowage** module lets you manage tank plans and cargo operations.

**Key features:**
- View and edit the vessel's tank plan layout
- Manage cargo loading/discharging sequences
- Track tank cleaning status
- Integration with external stowage planning tools

To access Stowage, select a vessel and click the **Stowage** icon in the left navigation panel.`,

    temperature: `**Cargo Temperature Monitoring** in OrcaWeb:

Vessel officers log temperatures for each cargo tank. The system checks against:
- **Commodity book** min/max thresholds
- **IBC code** requirements
- **Adjacent tank** influences

If a temperature exceeds limits, an alert is generated for the QC analysts onshore.

> 🔜 A new **heat map visualization** is planned that will overlay temperature data directly on the tank plan.`,
};

const SUGGESTIONS: ChatSuggestion[] = [
    { label: '🧭 How do I navigate OrcaWeb?', prompt: 'How do I navigate OrcaWeb?' },
    { label: '📋 Help with Report Events', prompt: 'How do I use Report Events?' },
    { label: '🌡️ Cargo temperature monitoring', prompt: 'Tell me about cargo temperature monitoring' },
    { label: '📦 Stowage overview', prompt: 'How does the Stowage module work?' },
];

function generateId(): string {
    return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function getMockResponse(userMessage: string): string {
    const lower = userMessage.toLowerCase();
    if (lower.includes('help') || lower.includes('what can you')) return MOCK_RESPONSES.help;
    if (lower.includes('stowage') || lower.includes('tank plan')) return MOCK_RESPONSES.stowage;
    if (lower.includes('temperature') || lower.includes('cargo temp')) return MOCK_RESPONSES.temperature;
    return MOCK_RESPONSES.default;
}

export default function useAiChat() {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    const sendMessage = useCallback(
        async (content: string) => {
            if (!content.trim() || isLoading) return;

            const userMessage: ChatMessage = {
                id: generateId(),
                role: 'user',
                content: content.trim(),
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setIsLoading(true);

            // Simulate streaming delay
            const assistantId = generateId();
            const fullResponse = getMockResponse(content);
            const words = fullResponse.split(' ');

            const assistantMessage: ChatMessage = {
                id: assistantId,
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                isStreaming: true,
            };

            setMessages((prev) => [...prev, assistantMessage]);

            // Simulate word-by-word streaming
            abortRef.current = new AbortController();
            let accumulated = '';

            for (let i = 0; i < words.length; i++) {
                if (abortRef.current?.signal.aborted) break;

                accumulated += (i === 0 ? '' : ' ') + words[i];
                const currentContent = accumulated;

                await new Promise((resolve) => setTimeout(resolve, 30 + Math.random() * 40));

                setMessages((prev) => prev.map((msg) => (msg.id === assistantId ? { ...msg, content: currentContent } : msg)));
            }

            // Mark streaming complete
            setMessages((prev) => prev.map((msg) => (msg.id === assistantId ? { ...msg, isStreaming: false } : msg)));

            setIsLoading(false);
            abortRef.current = null;
        },
        [isLoading]
    );

    const stopGenerating = useCallback(() => {
        abortRef.current?.abort();
        setIsLoading(false);
        setMessages((prev) => prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)));
    }, []);

    const clearMessages = useCallback(() => {
        stopGenerating();
        setMessages([]);
    }, [stopGenerating]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    return {
        messages,
        isLoading,
        isOpen,
        suggestions: messages.length === 0 ? SUGGESTIONS : [],
        sendMessage,
        stopGenerating,
        clearMessages,
        toggleOpen,
        open,
        close,
    };
}
